/**
 * The backend types `PreviewResponse.preview` as `dict[str, Any]`, so the
 * generated TS surface is `object`. These mirror the Pydantic shape from
 * `aria-web/src/backend_service/services/billing_ops/types.py`.
 */

export type Reversibility = 'reversible' | 'irreversible'

export interface FieldDiff {
  path: string
  before: unknown
  after: unknown
}

export interface SideEffect {
  kind: string
  description: string
}

export interface CustomerArtifact {
  kind: string
  description: string
  stripe_id?: string | null
}

export interface CustomerImpact {
  summary: string
  artifacts: CustomerArtifact[]
}

export interface BillingOpsPreviewView {
  customer_impact: CustomerImpact
  state_diff: FieldDiff[]
  side_effects: SideEffect[]
  reversibility: Reversibility
}

export interface AddPoToInvoiceInput {
  invoice_id: string
  po_number: string
}

export interface SetInvoiceStatementDescriptorInput {
  invoice_id: string
  statement_descriptor: string
}

export interface StripeAddress {
  line1?: string | null
  line2?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
}

export interface StripeShipping {
  name: string
  address: StripeAddress
  phone?: string | null
}

/**
 * Mirrors the backend ``OverrideBundle`` Pydantic model. The customer-level
 * fields (``customer_name``, ``customer_address``, ``customer_shipping``,
 * ``customer_email``, ``customer_phone``) are gated behind
 * ``applies_to_future_invoices`` because Stripe stores those on the Customer
 * object — any change necessarily propagates to all future invoices for the
 * customer. ``customer_tax_ids`` is not surfaced here because Stripe manages
 * tax ids as separate objects (``Customer.create_tax_id`` /
 * ``delete_tax_id``), not via ``Customer.modify``.
 */
export interface RecreateOverrideBundle {
  po_number?: string
  customer_name?: string
  customer_address?: StripeAddress
  customer_shipping?: StripeShipping
  customer_email?: string
  customer_phone?: string
  applies_to_future_invoices?: boolean
}

export interface RecreateSubscriptionInvoiceInput {
  invoice_id: string
  overrides: RecreateOverrideBundle
}

export interface ConvertToSalesOwnedInput {
  seats: number
  seat_cap: number | null
}

export type NoInput = Record<string, never>

export type OperationInput =
  | AddPoToInvoiceInput
  | SetInvoiceStatementDescriptorInput
  | RecreateSubscriptionInvoiceInput
  | ConvertToSalesOwnedInput
  | NoInput

export const OPERATION_KINDS = {
  ADD_PO: 'add_po_to_invoice',
  SET_DESCRIPTOR: 'set_invoice_statement_descriptor',
  RECREATE_SUB_INVOICE: 'recreate_subscription_invoice',
  CONVERT_TO_SALES_OWNED: 'convert_to_sales_owned',
  MOVE_TO_NEW_BILLING: 'move_to_new_billing',
  ROLL_BACK_NEW_BILLING_MOVE: 'roll_back_new_billing_move',
} as const

export const ENGINEERING_ONLY_KINDS: ReadonlySet<string> = new Set([
  OPERATION_KINDS.MOVE_TO_NEW_BILLING,
  OPERATION_KINDS.ROLL_BACK_NEW_BILLING_MOVE,
])
